/**
 * Billing > Invoices (Phase 21 / Task 21.7c): the list with its filters, the monthly runs,
 * and a new invoice (preview first, then create the draft).
 *
 *   GET  /v1/platform/billing/invoices?status=&customer=&month=
 *   GET  /v1/platform/billing/invoice-runs
 *   POST /v1/platform/billing/invoices/run        {month}
 *   POST /v1/platform/billing/invoices/preview    {customerTenantId, month}  writes nothing
 *   POST /v1/platform/billing/invoices            same body: the DRAFT
 *
 * A month is billable once it has ended in the billing time zone; the API refuses earlier.
 */
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ariaSort, listFrom, sortRows, useUrlParam, useUrlSort } from '../../lib/url-state';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { SortButton } from 'src/components/ui/sortable-head';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  invoiceListQuery,
  useBillingCustomers,
  useBillingToday,
  useInvoiceMutation,
  useInvoiceRuns,
  useInvoices,
  type Invoice,
  type InvoicePreview,
  type InvoiceRun,
  type InvoiceStatus,
} from './billing-hooks';
import { billingErrorText } from './billing-format';
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_TONES,
  money,
  previousMonth,
  skipReasonText,
} from './invoice-format';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** The columns the invoice list sorts by (OPEN-204); the API sends the newest month first. */
const INVOICE_SORTS = ['number', 'customer', 'month', 'due', 'total'] as const;

const box: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '16px',
  marginBottom: '16px',
};

function RunHistory({ defaultMonth }: { defaultMonth: string }): React.JSX.Element {
  const runs = useInvoiceRuns();
  const [month, setMonth] = React.useState(defaultMonth);
  const [open, setOpen] = React.useState<string | null>(null);
  const run = useInvoiceMutation<{ month: string }, { claimed: boolean; run: InvoiceRun }>(
    '/invoices/run',
    'POST',
    {
      onSuccess: () => undefined,
      onError: (err) => toast.error(billingErrorText(err, 'Could not run the month.')),
    },
  );
  return (
    <section style={box}>
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 10px' }}>Monthly runs</h3>
      <form
        onSubmit={(e): void => {
          e.preventDefault();
          if (month === '') return;
          run.mutate(
            { month },
            {
              onSuccess: (res) =>
                toast.success(
                  res.claimed
                    ? `${month} run: ${String(res.run.draftsCreated)} drafts created.`
                    : `${month} was already run; nothing new.`,
                ),
            },
          );
        }}
        style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}
      >
        <input
          type="month"
          aria-label="Run month"
          value={month}
          onChange={(e): void => setMonth(e.target.value)}
          style={{ ...fieldStyle, width: 'auto' }}
        />
        <Button type="submit" disabled={month === '' || run.isPending}>
          Run month
        </Button>
        <span style={subText}>Creates drafts for every billed customer. Nothing is sent.</span>
      </form>
      {runs.data === undefined ? (
        <p style={subText}>{runs.isError ? 'Could not load the runs.' : 'Loading…'}</p>
      ) : runs.data.runs.length === 0 ? (
        <p style={subText}>No month has been run yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ textAlign: 'left', ...subText }}>
              <th style={{ padding: '4px 6px' }}>Month</th>
              <th style={{ padding: '4px 6px' }}>By</th>
              <th style={{ padding: '4px 6px' }}>Customers</th>
              <th style={{ padding: '4px 6px' }}>Drafts</th>
              <th style={{ padding: '4px 6px' }}>Skipped</th>
            </tr>
          </thead>
          <tbody>
            {runs.data.runs.map((r) => (
              <React.Fragment key={r.periodStart}>
                <tr
                  data-testid={`run-${r.periodStart}`}
                  style={{ borderTop: '1px solid var(--color-border-primary)' }}
                >
                  <td style={{ padding: '6px' }}>{r.periodStart.slice(0, 7)}</td>
                  <td style={{ padding: '6px' }}>
                    {r.source === 'SCHEDULED' ? 'Schedule' : 'By hand'}
                  </td>
                  <td style={{ padding: '6px' }}>{r.customersConsidered}</td>
                  <td style={{ padding: '6px' }}>{r.draftsCreated}</td>
                  <td style={{ padding: '6px' }}>
                    {r.skipped.length === 0 ? (
                      '0'
                    ) : (
                      <Button
                        type="button"
                        variant="link"
                        onClick={(): void => setOpen(open === r.periodStart ? null : r.periodStart)}
                      >
                        {r.skipped.length} — {open === r.periodStart ? 'hide' : 'why'}
                      </Button>
                    )}
                  </td>
                </tr>
                {open === r.periodStart && (
                  <tr>
                    <td colSpan={5} style={{ padding: '4px 6px 10px' }}>
                      <ul style={{ margin: 0, paddingLeft: '18px', ...subText }}>
                        {r.skipped.map((s) => (
                          <li key={s.customerTenantId}>
                            {s.customerName}: {skipReasonText(s.reason)}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function NewInvoice({ defaultMonth }: { defaultMonth: string }): React.JSX.Element {
  const navigate = useNavigate();
  const customers = useBillingCustomers();
  const [customer, setCustomer] = React.useState('');
  const [month, setMonth] = React.useState(defaultMonth);
  const [preview, setPreview] = React.useState<InvoicePreview | null>(null);
  const onError = (err: unknown): void => {
    toast.error(billingErrorText(err, 'Could not do that.'));
  };
  const doPreview = useInvoiceMutation<{ customerTenantId: string; month: string }, InvoicePreview>(
    '/invoices/preview',
    'POST',
    { onSuccess: () => undefined, onError },
  );
  const create = useInvoiceMutation<{ customerTenantId: string; month: string }, Invoice>(
    '/invoices',
    'POST',
    { onSuccess: () => undefined, onError },
  );
  const body = { customerTenantId: customer, month };
  const ready = customer !== '' && month !== '';
  return (
    <section style={box}>
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 10px' }}>New invoice</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '10px' }}>
        <FormRow label="Customer" htmlFor="ni-customer">
          <select
            id="ni-customer"
            value={customer}
            onChange={(e): void => {
              setCustomer(e.target.value);
              setPreview(null);
            }}
            style={fieldStyle}
          >
            <option value="">Choose…</option>
            {(customers.data?.customers ?? []).map((c) => (
              <option key={c.tenantId} value={c.tenantId}>
                {c.tenantName}
              </option>
            ))}
          </select>
        </FormRow>
        <FormRow label="Month" htmlFor="ni-month">
          <input
            id="ni-month"
            type="month"
            value={month}
            onChange={(e): void => {
              setMonth(e.target.value);
              setPreview(null);
            }}
            style={fieldStyle}
          />
        </FormRow>
        <div style={{ alignSelf: 'end' }}>
          <Button
            type="button"
            variant="outline"
            disabled={!ready || doPreview.isPending}
            onClick={(): void => doPreview.mutate(body, { onSuccess: (p) => setPreview(p) })}
          >
            Preview
          </Button>
        </div>
      </div>
      {preview !== null && (
        <div data-testid="invoice-preview" style={{ marginTop: '12px' }}>
          {preview.skipReason !== null ? (
            <p role="alert" style={{ color: 'var(--color-danger)' }}>
              No invoice: {skipReasonText(preview.skipReason)}.
            </p>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <tbody>
                  {preview.lines.map((l, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                      <td style={{ padding: '4px 6px' }}>{l.description}</td>
                      <td style={{ padding: '4px 6px' }}>{l.quantity}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                        {money(l.amountCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ textAlign: 'right', fontWeight: 600 }}>
                Total {money(preview.totalCents)}
              </p>
            </>
          )}
          {preview.warnings.map((w) => (
            <p key={w.code} style={{ ...subText, color: 'var(--color-warning)' }}>
              {w.message}
            </p>
          ))}
          {preview.skipReason === null && (
            <Button
              type="button"
              disabled={create.isPending}
              onClick={(): void =>
                create.mutate(body, {
                  onSuccess: (inv) => {
                    toast.success('Draft created. Nothing is sent until you press Send.');
                    void navigate(`/billing/invoices/${inv.id}`);
                  },
                })
              }
            >
              Create draft
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

export function InvoicesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const today = useBillingToday();
  const customers = useBillingCustomers();
  const location = useLocation();
  // OPEN-191 — the filters live in the URL, so an invoice's Back returns to this exact list.
  const [status, setStatus] = useUrlParam('status', '', []);
  const [month, setMonth] = useUrlParam('month', '', []);
  const [customer, setCustomer] = useUrlParam('customer', '', []);
  const list = useInvoices(invoiceListQuery({ status, month, customer }));
  const { sort, toggle } = useUrlSort(INVOICE_SORTS, { key: 'month', dir: 'desc' }, []);
  const invoices = sortRows(list.data?.invoices ?? [], sort, (i, k) => {
    switch (k) {
      case 'number':
        return i.invoiceNumber;
      case 'customer':
        return i.customerName ?? i.customerTenantId;
      case 'month':
        return i.month;
      case 'due':
        return i.dueDate;
      case 'total':
        return i.totalCents;
    }
  });

  if (today === null) return <p style={subText}>Loading…</p>;
  const defaultMonth = previousMonth(today);

  return (
    <div>
      <RunHistory defaultMonth={defaultMonth} />
      <NewInvoice defaultMonth={defaultMonth} />
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <select
          aria-label="Status"
          value={status}
          onChange={(e): void => setStatus(e.target.value)}
          style={{ ...fieldStyle, width: 'auto' }}
        >
          <option value="">Any status</option>
          {(Object.keys(INVOICE_STATUS_LABELS) as InvoiceStatus[]).map((s) => (
            <option key={s} value={s}>
              {INVOICE_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <input
          type="month"
          aria-label="Month"
          value={month}
          onChange={(e): void => setMonth(e.target.value)}
          style={{ ...fieldStyle, width: 'auto' }}
        />
        <select
          aria-label="Customer"
          value={customer}
          onChange={(e): void => setCustomer(e.target.value)}
          style={{ ...fieldStyle, width: 'auto' }}
        >
          <option value="">Any customer</option>
          {(customers.data?.customers ?? []).map((c) => (
            <option key={c.tenantId} value={c.tenantId}>
              {c.tenantName}
            </option>
          ))}
        </select>
      </div>
      {list.isLoading ? (
        <p style={subText}>Loading…</p>
      ) : list.isError || list.data === undefined ? (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the invoices.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ textAlign: 'left', ...subText }}>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'number')}>
                <SortButton column="number" sort={sort} onSort={toggle}>
                  Number
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'customer')}>
                <SortButton column="customer" sort={sort} onSort={toggle}>
                  Customer
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'month')}>
                <SortButton column="month" sort={sort} onSort={toggle}>
                  Month
                </SortButton>
              </th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px' }} aria-sort={ariaSort(sort, 'due')}>
                <SortButton column="due" sort={sort} onSort={toggle}>
                  Due
                </SortButton>
              </th>
              <th
                style={{ padding: '6px 8px', textAlign: 'right' }}
                aria-sort={ariaSort(sort, 'total')}
              >
                <SortButton column="total" sort={sort} onSort={toggle}>
                  Total
                </SortButton>
              </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr
                key={i.id}
                data-testid={`invoice-${i.id}`}
                onClick={(): void => {
                  void navigate(`/billing/invoices/${i.id}`, { state: listFrom(location) });
                }}
                style={{ cursor: 'pointer', borderTop: '1px solid var(--color-border-primary)' }}
              >
                <td style={{ padding: '8px' }}>{i.invoiceNumber ?? '—'}</td>
                <td style={{ padding: '8px' }}>{i.customerName ?? i.customerTenantId}</td>
                <td style={{ padding: '8px' }}>{i.month}</td>
                <td style={{ padding: '8px' }}>
                  <Pill
                    tone={INVOICE_STATUS_TONES[i.status]}
                    label={INVOICE_STATUS_LABELS[i.status]}
                  />{' '}
                  {i.emailStatus === 'FAILED' && <Pill tone="danger" label="Email failed" />}
                  {i.warningCount > 0 && (
                    <Pill tone="warning" label={`${formatNumber(i.warningCount)} warning(s)`} />
                  )}
                </td>
                <td style={{ padding: '8px' }}>{i.dueDate ?? '—'}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{money(i.totalCents)}</td>
              </tr>
            ))}
            {list.data.invoices.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '12px 8px', ...subText }}>
                  No invoices match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
